import actionTypes from '../actions/actionTypes'
import findParentTree from 'helpers/filter/findParentTree'
import searchStatuses from 'helpers/enums/storeSearchStatuses'
import filterTypes from 'helpers/enums/filters/filterTypes'
import displayTypes from 'helpers/enums/filters/displayTypes'
import filterTabs from 'helpers/enums/filters/tabs'
import lessonFilterTypes from 'helpers/enums/filters/lessonFilterTypes'
import {findItemInCategory, findFilterParentCategory} from 'helpers/filter/findInCategory'

const defaultState = {
	categories: [],
	standard: [],
	selectedFilters: [],
	confirmedFilters: [],
	searchStatus: searchStatuses.INIT,
	hasMoreResults: false,
	page: 0,
	results: [],
	currentTab: filterTabs.REGULAR_TAB,
	query: '',
	last_std_selected: 0,
	folderTitle: '',
	folderUID: 0,
	lessonTypeSelectedFilter: {
		[lessonFilterTypes.REGULAR]: false,
		[lessonFilterTypes.VIDEO]: false,
	},
}

function updateStoreFilters(state, action) {
	return {
		...state,
		categories: action.filters.categories,
		standard: action.filters.standard,
		last_std_selected: action.filters.last_std_selected || state.last_std_selected,
		hasAccessToFilterTypes: action.filters.canFilterTypes,
	}
}

function addStandardToFilters(state, action) {
	const standardStates = state.standard[0].items
	const standardIndex = standardStates.findIndex(item => item.entityUID === action.standard.entityUID)
	if (standardIndex >= 0) {
		const newStateStandards = [...standardStates.slice(0, standardIndex), action.standard, ...standardStates.slice(standardIndex + 1, standardStates.length)]
		const newStandard = [{...state.standard[0], items: newStateStandards}, ...state.standard.slice(1, state.standard.length)]
		return {
			...state,
			standard: newStandard,
		}
	}
	return {
		...state,
	}
}

function findFilterInCategoryItems(items, entityUID) {
	let i = 0, category
	const len = items.length
	while (i < len) {
		if (items[i].entityUID === entityUID) {
			return items[i]
		}
		if (items[i].items) {
			category = findFilterParentCategory(items[i].items, entityUID)
			if (category) {
				return category
			}
		}
		i += 1
	}
	const index = items.findIndex(item => item.entityUID === entityUID)
	if (index === -1) {
		return null
	}
	return items[index]
}

function cleanParentedCategories(categories, selectedFilters, entityUID) {
	const parentTree = findParentTree(entityUID, categories)
	return selectedFilters.filter(entityUID => parentTree.indexOf(entityUID) === -1)
}

function cleanSiblingFilters(categories, selectedFilters, entityUID) {
	const filterParentCategory = findFilterParentCategory(categories, entityUID)
	return selectedFilters.filter(entityUID => !findItemInCategory(filterParentCategory, entityUID))
}

function updateLastStandardSelected(last_std_selected, entityUID, items) {
	let i = 0
	const len = items.length
	while (i < len) {
		if (items[i].entityUID === entityUID) {
			return entityUID
		}
		i += 1
	}
	return last_std_selected
}

function addFilterToSelectedFilters(state, action) {
	const entityUID = action.id
	let selectedFilters = state.selectedFilters
	const filterSelectedIndex = selectedFilters.indexOf(entityUID)

	const newState = {...state}
	if (action.filterType === filterTypes.STANDARDS_FILTER) {
		const standardFilter = findFilterInCategoryItems(state.standard[0].items, entityUID)
		const displayType = standardFilter ? standardFilter.displayType : null
		const canUnselect = action.canUnselect && displayType && ![displayTypes.DROPDOWN_WITH_CHILDS, displayTypes.DROPDOWN_WITHOUT_CHILDS].includes(displayType)

		if (canUnselect) {
			// Find leaf node in the branch that includes the selected filter
			const lastChildSelectedUID = selectedFilters.find(id => {
				const parent = findParentTree(id, state.standard)
				return parent.includes(entityUID) || id === entityUID
			})

			if (lastChildSelectedUID) {
				// Hierarchy tree with all filters of the branch including the selected one at the end
				const entityTree = findParentTree(lastChildSelectedUID, state.standard).concat([entityUID])
				const parentUID = entityTree[entityTree.indexOf(entityUID) - 1]
				const lastChildSelectedIndex = selectedFilters.indexOf(lastChildSelectedUID)

				// Add to selected filters the direct parent of the filter that was just unselected
				selectedFilters = [...selectedFilters.slice(0, lastChildSelectedIndex), parentUID, ...selectedFilters.slice(lastChildSelectedIndex+1)]
				newState.selectedFilters = selectedFilters
				return newState
			}
		}

		if (filterSelectedIndex !== -1) {
			return state
		}

		selectedFilters = cleanSiblingFilters(state.standard, selectedFilters, entityUID)
		selectedFilters = cleanParentedCategories(state.standard, selectedFilters, entityUID)
		selectedFilters.push(entityUID)
		newState.last_std_selected = updateLastStandardSelected(newState.last_std_selected, entityUID, state.standard[0].items)
	} else {
		if (filterSelectedIndex === -1) {
			selectedFilters = [...selectedFilters, entityUID]
		} else {
			selectedFilters = [...selectedFilters.slice(0, filterSelectedIndex), ...selectedFilters.slice(filterSelectedIndex + 1, selectedFilters.length)]
		}
	}
	newState.selectedFilters = selectedFilters
	return newState
}

function updateFilters(state, action) {
	return {...state,
		selectedFilters: action.filters,
		confirmedFilters: action.filters,
		currentTab: action.tab,
		query: action.query,
	}
}

function setSearchLessons(state, action) {
	return {
		...state,
		results: [
			...state.results,
			...action.entities.map(entity => entity.data.entityUID),
		],
		searchStatus: searchStatuses.READY,
		hasMoreResults: action.hasMore,
		page: action.page,
	}
}

function setFolderLessons(state, action) {
	return {
		...setSearchLessons(state, action),
		folderTitle: action.folderTitle,
	}

}

function updateSearchFilters(state) {
	return {
		...state,
		confirmedFilters: [...state.selectedFilters],
	}
}

function toggleFiltersTab(state, action) {
	if (action.tab === state.currentTab) {
		return state
	}
	const selectedFilters = []
	if (action.tab === '1' && state.last_std_selected) {
		selectedFilters.push(state.last_std_selected)
	}
	return {
		...state,
		currentTab: action.tab,
		selectedFilters,
		confirmedFilters: [],
	}
}

function updateQuery(state, action) {
	const shouldChangeToLoading = action.type === actionTypes.STORE_QUERY_SEARCH
	const newSearchStatus = shouldChangeToLoading ? searchStatuses.LOADING : state.searchStatus
	return {
		...state,
		query: action.query,
		searchStatus: newSearchStatus,
	}
}

function clearSelectedFilters(state) {
	return {
		...state,
		selectedFilters: [],
		confirmedFilters: [],
	}
}

function clearSelectedStandardFilters(state) {
	return {
		...state,
		selectedFilters: [],
		confirmedFilters: [],
		currentTab: filterTabs.REGULAR_TAB,
	}
}

function clearSelectedStandardAndQueryFilters(state) {
	return {
		...state,
		selectedFilters: [],
		confirmedFilters: [],
		query: '',
		currentTab: filterTabs.REGULAR_TAB,
		lessonTypeSelectedFilter: {
			[lessonFilterTypes.REGULAR]: false,
			[lessonFilterTypes.VIDEO]: false,
		},
	}
}

function updateCatalog(state, action) {
	const storefrontBar = (action.catalog.storefrontBar && action.catalog.storefrontBar.message)
		? action.catalog.storefrontBar.message
		: ''
	return {
		...state,
		catalog: {
			storefrontBar,
			widgets: action.catalog.widgets,
		},
	}
}

function intializeSearchRoute(state, action) {
	const newState = {...state}
	newState.query = action.params && action.params.q || ''
	newState.results = []
	newState.hasMoreResults = false
	newState.searchStatus = searchStatuses.INIT,
	newState.page = 0
	newState.lessonTypeSelectedFilter = {
		[lessonFilterTypes.REGULAR]: false,
		[lessonFilterTypes.VIDEO]: false,
	}
	if (action.params && action.params.lessonTypeFilter) {
		action.params.lessonTypeFilter.map(lessonType => {
			newState.lessonTypeSelectedFilter[lessonType] = true
		})
	}
	return newState
}

function intializeFolderRoute(state, action) {
	const newState = {...state}
	newState.results = []
	newState.hasMoreResults = false
	newState.searchStatus = searchStatuses.INIT
	newState.page = 0
	newState.folderUID = action.folderUID
	return newState
}

function getNextStorePage(state) {
	return {
		...state,
		page: state.page + 1,
		searchStatus: searchStatuses.LOADING_MORE,
	}
}

function clearSearchParams(state) {
	return {
		...state,
		currentTab: filterTabs.REGULAR_TAB,
		selectedFilters: [],
		confirmedFilters: [],
		query: '',
		lessonTypeSelectedFilter: {
			[lessonFilterTypes.REGULAR]: false,
			[lessonFilterTypes.VIDEO]: false,
		},
	}
}

function updateLessonTypeFilter(state, action) {
	return {
		...state,
		lessonTypeSelectedFilter: {
			...state.lessonTypeSelectedFilter,
			[action.value]: !state.lessonTypeSelectedFilter[action.value],
		},
	}
}

function updateResultsWithEntityDownloaded(state, action) {
	const results = [...state.results]
	const entityIndex = results.indexOf(action.entityUID)
	const newEntityUid = action.payload && action.payload.presentation && action.payload.presentation.data.entityUID
	if (entityIndex && newEntityUid) {
		results.splice(entityIndex, 1, newEntityUid)
	}

	return {
		...state,
		results,
	}
}

const storeReducer = (state = defaultState, action) => {
	switch (action.type) {
		case actionTypes.STORE_FILTERS_FETCHED:
			return updateStoreFilters(state, action)
		case actionTypes.STORE_STANDARD_FETCHED:
			return addStandardToFilters(state, action)
		case actionTypes.STORE_FILTER_SELECTED:
			return addFilterToSelectedFilters(state, action)
		case actionTypes.STORE_STANDARD_FILTERS_CLEAR:
			return clearSelectedStandardFilters(state)
		case actionTypes.STORE_STANDARD_AND_QUERY_FILTERS_CLEAR:
			return clearSelectedStandardAndQueryFilters(state)
		case actionTypes.STORE_FILTERS_SEARCH:
			return updateSearchFilters(state)
		case actionTypes.STORE_UPDATE_FILTERS:
			return updateFilters(state, action)
		case actionTypes.STORE_CONNECTOR_SEARCH_INITIALIZE:
		case actionTypes.STORE_CONNECTOR_SEARCH_UPDATE:
		case actionTypes.STORE_SEARCH_GET_RESULTS_SUCCESS:
			return setSearchLessons(state, action)
		case actionTypes.STORE_FOLDER_GET_RESULTS_SUCCESS:
			return setFolderLessons(state, action)
		case actionTypes.STORE_FILTERS_TAB_SELECTED:
			return toggleFiltersTab(state, action)
		case actionTypes.STORE_QUERY_SEARCH:
		case actionTypes.STORE_QUERY_INPUT_CHANGE:
			return updateQuery(state, action)
		case actionTypes.STORE_SEARCH_GET_NEXT_PAGE:
		case actionTypes.STORE_FOLDER_GET_NEXT_PAGE:
			return getNextStorePage(state)
		case actionTypes.STORE_CLEAR_SELECTED_FILTERS:
		case actionTypes.STORE_FILTERS_CLEAR:
			return clearSelectedFilters(state)
		case actionTypes.STORE_CATALOG_GET_RESULTS_SUCCESS:
			return updateCatalog(state, action)
		case actionTypes.STORE_SEARCH_ROUTE_INITIALIZE:
		case actionTypes.STORE_SEARCH_ROUTE_UPDATE:
		case actionTypes.VIDEO_STORE_PICKER_INITIALIZED:
		case actionTypes.VIDEO_STORE_PICKER_UPDATED:
		case actionTypes.GAME_INITIALIZE_CREATION_TOOL:
		case actionTypes.GAME_INITIALIZE_STANDALONE:
		case actionTypes.GAME_UPDATE_CREATION_TOOL_SEARCH:
		case actionTypes.DRAW_IT_INITIALIZE_CREATION_TOOL:
		case actionTypes.DRAW_IT_UPDATE_CREATION_TOOL_SEARCH:
		case actionTypes.DRAW_IT_INITIALIZE_STANDALONE:
		case actionTypes.MATCHING_PAIRS_CT_INITIALIZE_ROUTE:
		case actionTypes.MATCHING_PAIRS_UPDATE_CREATION_TOOL_SEARCH:
		case actionTypes.MATCHING_PAIRS_INITIALIZE_STANDALONE:
			return intializeSearchRoute(state, action)
		case actionTypes.STORE_FOLDER_INITIALIZE:
			return intializeFolderRoute(state, action)
		case actionTypes.STORE_LANDING_INITIALIZE:
			return clearSearchParams(state)
		case actionTypes.STORE_LESSON_TYPE_FILTER_SELECTED:
			return updateLessonTypeFilter(state, action)
		case actionTypes.CART_BUY_PRODUCT_SUCCESS:
			return updateResultsWithEntityDownloaded(state, action)
		default:
			return state
	}
}

export default storeReducer

export {
	defaultState,
}
